import React, { useEffect, useState } from 'react';
// NOTE: the original 3D R3F ship lives in HologramShip.jsx — kept intact so
// we can revert with a single import swap if the user types "N".
import ShipVideo from '@/components/ShipVideo';
import SkullVideo from '@/components/SkullVideo';
import { DECK } from '@/constants/testIds';

// HologramStage manages the fixed sticky 3D-hologram layer. The deck has two
// distinct hologram phases:
//   - SHIP (segments 01–12): a translucent CSS-based hologram of the cyan
//     wireframe burning cargo ship reference. Slowly drifts/floats at the right
//     edge. Brightness + saturation climb on scroll; a fire-burst overlay
//     ignites during the climactic segments before handing off to the skull.
//   - SKULL (segments 13–16): the cinematic skull GLB with the bullet
//     pass-through animation, drawn in a true 3D Canvas.
//
// The cold open (segment 00) shows neither — only the poster.
export default function HologramStage({ progressRef, segmentTargets }) {
  const [stage, setStage] = useState('ship');

  useEffect(() => {
    let raf;
    const tick = () => {
      const p = progressRef.current || 0;
      let next = 'ship';
      if (p < 0.05) next = 'none';
      else if (p < 0.21) next = 'ship';
      else if (p < 0.94) next = 'none';
      else if (p < 0.99) next = 'skull';
      else next = 'none';
      setStage((prev) => (prev === next ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => raf && cancelAnimationFrame(raf);
  }, [progressRef]);

  return (
    <div
      data-testid={DECK.stickySkullStage}
      className="pointer-events-none fixed inset-0 z-10"
      aria-hidden="true"
    >
      {/* CSS hologram ship — visible during pitch (atmospheric, JARVIS style) */}
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{ opacity: stage === 'ship' ? 1 : 0 }}
      >
        <ShipVideo progressRef={progressRef} active={stage === 'ship'} />
      </div>

      {/* Holographic skull (pre-rendered MP4) — replaces the previous 3D
          R3F SkullScene. Composited with mix-blend-mode: screen so the dark
          background of the video drops out cleanly over the deck. */}
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{ opacity: stage === 'skull' ? 1 : 0 }}
      >
        <SkullVideo progressRef={progressRef} segmentTargets={segmentTargets} active={stage === 'skull'} />
      </div>

      {/* Global scanline + soft halo overlay */}
      <div className="absolute inset-0 scanlines opacity-50 mix-blend-screen" />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(420px 360px at 78% 50%, rgba(64,180,255,0.08), rgba(64,180,255,0) 70%)',
        }}
      />
    </div>
  );
}
